/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { SelectChangeEvent } from '@mui/material';
import { LinearProgress, MenuItem } from '@mui/material';
import type { TFunction } from 'i18next';
import React from 'react';
import { useSelector } from 'react-redux';
import { TooltipSelect } from '@opengeoweb/shared';
import { getService } from '../../../store/selectors';
import type { TimeSeriesRootState } from '../../../store/types';
import { useGetDetailsForCollection } from '../../../utils/timeseries-api/hooks';
import type { Parameter, SelectParameter } from '../../TimeSeries/types';

const ParameterNameSelect: React.FC<{
  rowIsEnabled: boolean;
  parameter: Parameter;
  patchParameter: (parameter: Partial<Parameter>) => void;
  t: TFunction;
}> = ({ rowIsEnabled, parameter, patchParameter, t }) => {
  const { serviceId, collectionId } = parameter;

  const service = useSelector((store: TimeSeriesRootState) =>
    getService(store, serviceId),
  );

  const { data: collectionDetails } = useGetDetailsForCollection(
    service!,
    collectionId,
  );

  const parameterList = collectionDetails?.parameters || [];
  const isFetching = parameterList.length === 0;

  return (
    <TooltipSelect
      SelectDisplayProps={{ 'aria-label': 'timeseriesManager-parameterName' }}
      isEnabled={rowIsEnabled}
      onChange={(event: SelectChangeEvent<unknown>): void => {
        const selectedParam = parameterList.find((parameter) => {
          return parameter.propertyName === event.target.value;
        });
        if (selectedParam) {
          const newParameter = {
            id: parameter.id,
            propertyName: selectedParam.propertyName,
          };
          patchParameter(newParameter);
        } else {
          console.warn(`Unable to find param${event.target.value}`);
        }
      }}
      tooltip={`Parameter: ${parameter.propertyName}  ${parameter.propertyLabel ? '-' : ''} ${parameter.propertyLabel ? parameter.propertyLabel : ''}`}
      value={parameter.propertyName}
    >
      {!!isFetching && <LinearProgress color="inherit" data-testid="spinner" />}
      <MenuItem disabled>{t('timeseries-parameter')}</MenuItem>
      {parameterList.length === 0 && (
        <MenuItem value={parameter.propertyName}>
          {parameter.propertyName}
        </MenuItem>
      )}
      {parameterList.map((parameter: SelectParameter) => {
        return (
          <MenuItem
            key={`parametermenu${parameter.propertyName}`}
            value={parameter.propertyName}
          >
            {parameter.propertyLabel || parameter.propertyName}
          </MenuItem>
        );
      })}
    </TooltipSelect>
  );
};

export default ParameterNameSelect;
